import { cn } from "@exhousing/design-system";
import { ArrowRight, Check, X } from "lucide-react";
import { Fragment } from "react";

/** A horizontal progress + navigation rail for an entity lifecycle. Steps are computed
 *  by the page (states + milestones) and double as anchors into the relevant tab.
 *  Shared across domains (contracts, properties, …). */

export type StepState = "done" | "current" | "upcoming" | "cancelled";

export interface LifecycleStep {
  key: string;
  label: string;
  state: StepState;
  tab?: string; // where this step's work lives
}

function Node({
  state,
  label,
  onClick,
}: {
  state: StepState;
  label: string;
  onClick?: () => void;
}) {
  const body = (
    <>
      <span
        className={cn(
          "grid size-7 place-items-center rounded-full text-xs transition-colors",
          state === "done" && "bg-primary text-primary-foreground",
          state === "current" && "bg-primary text-primary-foreground ring-4 ring-primary/15",
          state === "upcoming" && "border border-border bg-card text-muted-foreground",
          state === "cancelled" && "bg-danger text-white",
        )}
      >
        {state === "done" && <Check className="size-3.5" />}
        {state === "current" && <span className="size-2 rounded-full bg-current" />}
        {state === "cancelled" && <X className="size-3.5" />}
      </span>
      <span
        className={cn(
          "absolute top-8 whitespace-nowrap text-xs",
          state === "upcoming" ? "text-muted-foreground" : "font-medium text-foreground",
          state === "cancelled" && "text-danger",
        )}
      >
        {label}
      </span>
    </>
  );

  const className = "relative flex shrink-0 flex-col items-center";
  if (!onClick) return <div className={className}>{body}</div>;
  return (
    <button
      type="button"
      onClick={onClick}
      title={`Go to ${label}`}
      className={cn(className, "transition-opacity hover:opacity-70")}
    >
      {body}
    </button>
  );
}

export function LifecycleStepper({
  steps,
  hint,
  onNavigate,
}: {
  steps: LifecycleStep[];
  hint?: string;
  onNavigate?: (tab: string) => void;
}) {
  return (
    <div>
      <div className="flex items-center pb-6">
        {steps.map((step, i) => {
          const next = steps[i + 1];
          const connector =
            step.state === "done"
              ? "bg-primary"
              : next?.state === "cancelled"
                ? "bg-danger/40"
                : "bg-border";
          return (
            <Fragment key={step.key}>
              <Node
                state={step.state}
                label={step.label}
                onClick={onNavigate && step.tab ? () => onNavigate(step.tab!) : undefined}
              />
              {next && <span className={cn("mx-1 h-0.5 flex-1 rounded", connector)} />}
            </Fragment>
          );
        })}
      </div>
      {hint && (
        <p className="flex items-start gap-1.5 text-sm text-muted-foreground">
          <ArrowRight className="mt-0.5 size-4 shrink-0 text-accent" />
          {hint}
        </p>
      )}
    </div>
  );
}
